<script>
  import { onDestroy, onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { notifications } from '@client/stores/notifications';

  import { logGroup } from '@client/utils/logger';

  import { ACTIVITY_EVENTS } from '@client/enums';

  import { formatTemplate } from '@common/utils/strings';

  const { addNotification } = getNotificationsContext();

  export let token;

  let socket = null;
  let reconnectCount = 0;
  let reconnectTimeout = null;

  const processResponse = (data) => {
    const event = ACTIVITY_EVENTS[data.verb === 'clicked' || data.verb === 'visited' ? data.objecttype : data.verb];

    let item = data?.note?.name;

    if (event.id === 'linkclick') {
      item = data?.note?.linkname;
    }

    if (event.id === 'viewed') {
      item = data?.msgsubject;
    }

    if (event.id === 'viewerlaunched' || event.id === 'downloaded') {
      item = data?.note?.filename;
    }

    const template = formatTemplate($format(event.notification), {
      recipient: data.recipient,
      item
    });

    const body = Notification?.permission === 'granted' ? template.replace(/(<b>|<\/b>)/g, '') : template;

    return {
      title: $format(event.label),
      options: {
        color: event.color,
        body
      }
    };
  };

  const fallbackNotification = (config) => {
    const {
      title = '',
      options: { body = '', color }
    } = config;

    addNotification({
      header: title,
      text: body,
      color,
      autohide: 15000,
      dismissable: true,
      position: 'top-right'
    });
  };

  const enableWebNotifications = async ({ title, options }) => {
    fallbackNotification({ title, options });

    if (!('Notification' in window) || Notification?.permission === 'denied') {
      return true;
    }

    if (Notification.permission === 'granted') {
      new Notification(title, options);
    } else {
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        new Notification(title, options);
      }
    }
  };

  const disconnectSocket = () => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (socket) {
      socket.off();
      socket.disconnect();
      socket = null;
    }
  };

  const connectSocket = () => {
    if ((!token && !io) || socket) {
      return true;
    }

    if (!window.io) {
      logGroup('error', 'Websockets', 'window.io is undefined', {});
      return true;
    }

    socket = window.io('https://www.sendergen.com');

    const activity = function (data) {
      logGroup('system', 'Websockets', 'Incoming Activity', data);
      const parsed = processResponse(data);

      if (parsed.title !== $format(ACTIVITY_EVENTS.sent.label)) {
        enableWebNotifications(parsed);
      }

      const activity = {
        ...data,
        utcts: new Date(data.utcts)
      };

      notifications.addNotification(activity);
    };

    const authenticated = () => {
      logGroup('system', 'Websockets', 'Authenticated');
      // Reset reconnect count on successful authentication
      reconnectCount = 0;
    };

    socket.on('connect', () => logGroup('success', 'Websockets', 'Connected'));

    socket.emit('authentication', { token });

    socket.on('authenticated', authenticated);

    socket.on('activity', activity);

    socket.on('disconnect', function () {
      logGroup('error', 'Websockets', 'Disconnected', {
        reconnect: reconnectCount
      });

      socket.off();
      socket = null;

      // Increment reconnect count and attempt reconnection with exponential backoff
      reconnectCount++;
      if (reconnectCount < 50) {
        const delay = Math.min(1000 * Math.pow(2, reconnectCount), 30000); // Max 30 seconds
        reconnectTimeout = setTimeout(() => {
          connectSocket();
        }, delay);
      }
    });
  };

  onMount(() => {
    connectSocket();
  });

  onDestroy(() => {
    disconnectSocket();
  });
</script>
